/* eslint-disable max-len */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { ReactSVG } from "react-svg";
import Loading from "react-fullscreen-loading";

// region Imports - Assets
import LogoFleet from "@assets/FleetLogo/fleetLogo.svg";
import IconObra from "@assets/stateIcons/Na obra.svg";
import { ReactComponent as ManutencaoIcon } from "@assets/icons/maintenance-vehicle.svg";
import { ReactComponent as AlertIconRed } from "@assets/icons/alerts/create-alert-speed-red.svg";
import { ReactComponent as AlertIconGreen } from "@assets/icons/alerts/create-alert-speed-green.svg";
import { ReactComponent as UnblockVehicleIcon } from "@assets/icons/unblocked-vehicle.svg";
import { ReactComponent as BlockVehicleIcon } from "@assets/icons/blocked-vehicle.svg";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
// endregion Imports - Assets
// region Imports - Utility
import $ from "jquery";
import _ from "lodash";
import utils from "@utils/useful-functions";
// endregion Imports - Utility
// region Imports - Material-UI
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Badge,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Grid,
  Typography
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
// endregion Imports - Material-UI
// region Imports - Languages
import useTranslation from "src/translations/useTranslation";
import {
  GlobalMessages,
  MapMessages,
  TimelineVehicleStatesMessages, TravelAlertFullSpeedMessage,
  VehicleAndTravelDetailsMessages, VehicleMaintenanceMessages
} from "@shared/languages/interfaces";
// region Imports - Constants and Enums
import { ParamsNames } from "@shared/constants/params-names.enum";
import { VehicleStatesTypesID } from "@shared/constants/vehicle-states-types.enum";
import { AlertTypesID, NonAlertEventTypesID } from "@shared/constants/alert-types.enum";
import { PolygonOptions, PolylineOptions } from "@shared/interfaces/map.interface";
import { Travel } from "@shared/interfaces/travel.interface";
import { TravelAnalytics } from "@shared/interfaces/travel-analytics.interface";
import { Vehicle } from "@shared/interfaces/vehicle.interface";
import { VehicleTypes, VehicleTypesID } from "@shared/constants/vehicle-types.enum";
import { Duration } from "@shared/interfaces/duration.interface";
// endregion Imports - Constants and Enums
// region Imports - Hooks
import { useAuth } from "@hooks/useAuth";
import { useParams } from "@hooks/useParams";
import { useToast } from "@hooks/useToast";
// endregion Imports - Hooks
// region Imports - Store
import { ScreenSize } from "@store/ducks/Screen/screen.type";
// endregion Imports - Store
// region Imports - Services
import api from "@services/api";
// endregion Imports - Services
// region Imports - Components
import AlertModalList from "@components/Alert/AlertModalList";
import TimeLineVehicleStates from "@components/TimeLines/TimeLineVehicleStates";
import TravelData from "@components/TravelData";
import AggregateTrucksTravelData from "@components/AggregateTrucksTravelData";
import VehicleStatus from "@components/VehicleStatus";
import ModalVehicleBlock from "@components/Modal/VehicleBlock";
import ModalVehicleMaintenance from "@components/Modal/VehicleMaintenance";
import WaterAdditionModalList from "@components/WaterAddition/WaterAdditionModalList";

import ModalSpeedTravel from "@components/Modal/SpeedTravelAlert";
import {
  TravelMap, TravelMarkerOptions, buildTravelRoute, generateAreaLabelOptions, generateCirclesOptions, generateDefaultMapFilter,
  generateMarkerOptions, generatePolygonsOptions, generatePolylinesOptions, readZoneOverlays
} from "@components/Map/TravelMap";
// endregion Imports - Components
// region Imports - Shared
import { TLanguages } from "@shared/languages/languageData";
import { GenericVehicle } from "@shared/interfaces/socket-dto.interface";
import { isVehicleAlertSummary } from "@shared/utils/useful-functions";
import { Alert, Alert as AlertInterface } from "@shared/interfaces/alert.interface";
// endregion Imports - Shared
// region Imports - Styles
import { ModalVehicleAndTravelDetailsStylesheet as Stylesheet } from "./style";
// endregion Imports - Styles

type ModalVehicleAndTravelDetailsProps = {
  vehicle: GenericVehicle;
  travel: Travel;
  open: boolean;
  onClose: () => void;
  historical?: boolean;
  overAll?: boolean;
  onlyView?: boolean;
  isBlocked?: boolean
};
const ModalVehicleAndTravelDetails: React.FC<ModalVehicleAndTravelDetailsProps> = ({
  vehicle: genericVehicle,
  travel,
  open,
  onClose,
  historical = false,
  overAll = false,
  onlyView = false,
  isBlocked = genericVehicle.blocked
}) => {

  // region Hooks
  const { addToast } = useToast();

  const improperDischargeAlerts = useSelector(({ alerts: state }) => state.all.list.filter(
    (alert) => alert.type.id_alert_type === AlertTypesID.DESCARREGAMENTO_INDEVIDO
  ));

  const screen = useSelector(({ screen: state }) => state);

  const { user } = useAuth();

  const { getParamsObj } = useParams();

  const { t, i18n } = useTranslation();
  // endregion Hooks
  // region States
  const [showMap, setShowMap] = useState(false);
  const [openModalVehicleBlock, setOpenModalVehicleBlock] = useState(false);
  const [openModalVehicleMaintenance, setOpenModalVehicleMaintenance] = useState(false);
  const [openModalCreateAlert, setOpenModalCreateAlertSpeed] = useState(false);

  const [travelAnalyticRecords, setTravelAnalyticRecords] = useState<TravelAnalytics[]>([]);
  const [travelDetails, setTravelDetails] = useState<Travel>({} as Travel);

  // Client's default construction radius, used to size the obra circle exactly as the worker does
  const [clientDefaultRadiusMeters, setClientDefaultRadiusMeters] = useState<number | null>(null);

  const [vehicle, setVehicle] = useState<Vehicle>(genericVehicle as Vehicle);

  const [isLoading, setIsLoading] = useState(false);

  const [alerts, setAlerts] = useState<AlertInterface[]>([]);

  const [loadingTravelMap, setLoadingTravelMap] = useState(false);
  const [mapFilter, setMapFilter] = useState(generateDefaultMapFilter(t, vehicle));
  // endregion States
  // region Constants
  const isMobile = screen.size !== ScreenSize.large;

  const isAggregateOrSiloTruck = vehicle.type.description === VehicleTypes.CARRETA_SILO
    || vehicle.type.description === VehicleTypes.CARRETA_AGREGADOS;

  const waterAdditions = travel?.water_additions?.map((waterAddition) => ({
    ...waterAddition,
    // water reassigned from a previous travel is reused in this load (added before this travel's
    // discharge) → counts as PRE-discharge here; it is still shown segmented by origin in the list.
    is_post_discharge: waterAddition?.is_reassigned_post_discharge
      ? false
      : (waterAddition?.start_date && travel?.last_due_unload_date)
        ? waterAddition?.start_date > travel?.last_due_unload_date
        : undefined
  }));
  // endregion Constants
  // region Functions

  const [polygons, setPolygons] = useState<PolygonOptions[]>([]);
  // Marker opened from the timeline (a new nonce reopens the same one)
  const [polylinesZMRC, setPolylinesZMRC] = useState<PolylineOptions[]>([]);

  /** Search details of selected travel to Analyze (just coords)
   * @param travel Target travel to be analyzed
   */
  const analyzeTravelDetail = useCallback(async (travel: Travel) => {

    if (!travel) {
      addToast({
        type: "info",
        title: t(VehicleAndTravelDetailsMessages.noActiveTravel),
        description: t(VehicleAndTravelDetailsMessages.noActiveTravelDescription)
      });
    } else {

      const travelDet: Travel = { ..._.clone(travel), vehicle: { ..._.clone(vehicle) } };
      let alertMessageContent = "";

      // Internal functions
      const FreadTravelAnalytics = async () => (await api.get("travels/get-travel-analytics",
        { params: { idTravel: travel.id_travel, fullAnalytics: false } })).data;
      const FreadTravelCoords = async () => (await api.get(`coords/read/${travel.id_travel}`)).data;

      // Show loading
      setLoadingTravelMap(true);

      // Read Zones
      readZones();

      try {

        const promise1 = FreadTravelAnalytics();
        const promise2 = travelDet?.destination ? await FreadTravelCoords() : await Promise.resolve();

        const [travelAnalyticsResponse, travelCoordsResponse] = await Promise.all([
          promise1, promise2
        ]);

        // Verify if have some alert in analytics data travel response
        if (travelAnalyticsResponse.status === "success") setTravelAnalyticRecords(travelAnalyticsResponse.result);
        else alertMessageContent += travelAnalyticsResponse.message;

        // Verify if we have Location Coords
        if (travelDet?.destination && travelCoordsResponse?.status === "success") {
          travelDet.coords = travelCoordsResponse.result;
          setTravelDetails(travelDet);
        } else alertMessageContent += travelCoordsResponse?.message ? travelCoordsResponse.message : "";

        // If we have some alert, show message to user
        if (!_.isEmpty(alertMessageContent)) {
          addToast({ type: "info", title: t(GlobalMessages.alert), description: alertMessageContent });
        } else {
          setShowMap(true);
          setTravelDetails(travelDet);
        }

      } catch (error) {
        if (!error.response) addToast({ type: "error", title: t(GlobalMessages.error), description: t(GlobalMessages.connectionNotEstablished) });
        else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });
      } finally {
        setLoadingTravelMap(false);
      }
    }

  }, [addToast, vehicle, t]);

  // Generate all map elements configured based in their types (markers, polylines, circles, filter)
  const markers: TravelMarkerOptions[] = useMemo(() => [
    ...generateMarkerOptions(travelDetails, travelAnalyticRecords, vehicle, t),
    ...generateAreaLabelOptions(travelDetails, clientDefaultRadiusMeters, t)
  ], [travelDetails, travelAnalyticRecords, vehicle, clientDefaultRadiusMeters, t]);

  // The route as recorded (positions in time order): drawn as the going/returning lines and played by the replay
  const route = useMemo(() => buildTravelRoute(travelAnalyticRecords), [travelAnalyticRecords]);

  // Route lines plus the ZMRC zones crossed
  const polylines = useMemo(() => [...generatePolylinesOptions(route.points), ...polylinesZMRC], [route, polylinesZMRC]);

  // Speed limit for the replay's speedometer: the travel's, else the vehicle's, read from its register when the
  // vehicle object at hand does not carry it (as in the travel history)
  const [vehicleSpeedLimit, setVehicleSpeedLimit] = useState<number | undefined>();

  useEffect(() => {
    if (!showMap || travelDetails?.alert_maximum_speed || vehicle?.maximum_speed_allowed || !vehicle?.id_vehicle) return undefined;

    let active = true;

    api.get(`vehicles/read/${vehicle.id_vehicle}`).then(({ data }) => {
      const found = Array.isArray(data?.result) ? data.result[0] : data?.result;

      if (active && Number(found?.maximum_speed_allowed) > 0) setVehicleSpeedLimit(Number(found.maximum_speed_allowed));
    }).catch(() => undefined);

    return () => {
      active = false;
    };
  }, [showMap, travelDetails?.alert_maximum_speed, vehicle?.maximum_speed_allowed, vehicle?.id_vehicle]);

  const destinationPoint = useMemo(() => (travelDetails?.destination?.latitude && travelDetails?.destination?.longitude
    ? { lat: Number(travelDetails.destination.latitude), lng: Number(travelDetails.destination.longitude) }
    : undefined), [travelDetails]);

  const circles = useMemo(
    () => generateCirclesOptions(travelDetails, clientDefaultRadiusMeters), [travelDetails, clientDefaultRadiusMeters]
  );
  const polygonPlaces = useMemo(() => generatePolygonsOptions(travelDetails), [travelDetails]);

  // Close modal actions
  const handleClose = useCallback(() => {
    onClose();
    setTimeout(() => { setShowMap(false); }, 1000);
  }, [onClose]);

  const handleOpenVehicleBlockModal = useCallback(() => {

    setOpenModalVehicleBlock(true);

  }, []);

  const handleOpenVehicleMaintenanceModal = useCallback(async () => {

    setOpenModalVehicleMaintenance(true);

  }, []);

  const handleOpenCreateAlertSpeedModal = useCallback(async () => {
    setOpenModalCreateAlertSpeed(true);
  }, []);

  const workHeaderContainer = useMemo(() => {

    let description = t(VehicleAndTravelDetailsMessages.noDescriptionRegistered);

    if (travel && travel.description) description = travel.description;

    if (isMobile) {

      return (
        <div className="block vehicle-travel-details__work-header">
          <div className="icon">
            <ReactSVG src={IconObra} />
          </div>
          <div className="content">
            <div className="title">{t(VehicleAndTravelDetailsMessages.construction)}</div>
            <div className="subtitle">
              { description }
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="block vehicle-travel-details__work-header">
        <div className="box">
          <div className="title">{t(VehicleAndTravelDetailsMessages.construction)}</div>
          <div className="text">
            { description }
          </div>
        </div>
      </div>
    );
  }, [isMobile, travel, t]);

  const statusContainer = useMemo(() => {

    if (!historical) {
      return (<VehicleStatus vehicle={vehicle} historical={historical} />);
    }

    return (<></>);
  }, [vehicle, historical]);

  const travelDataContainer = useMemo(() => {

    if (!$.isEmptyObject(travel)) {

      if (isAggregateOrSiloTruck) return (<AggregateTrucksTravelData travel={travel} vehicle={vehicle} />);

      return (<TravelData travel={travel} vehicle={vehicle} />);
    }

    return (<></>);
  }, [travel, vehicle, isAggregateOrSiloTruck]);

  const alertsContainer = useMemo(() => {

    if (vehicle) {
      return (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography component="div">
              <div className="title">{t(VehicleAndTravelDetailsMessages.alertsTitle)}</div>
              { alerts && (
                <div className="qtd">
                  <Badge
                    badgeContent={alerts.filter((alert) => !alert.confirmation_date).length}
                    color="error"
                  />
                </div>
              ) }
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography component="div">
              <div className="date">
                { !$.isEmptyObject(vehicle)
                && alerts
                && alerts.length > 0 ? (
                    <>
                      <div className="title">{t(GlobalMessages.date)}</div>
                      <div className="text">
                        { utils.formatDateIfHave(
                          alerts[0]?.event?.start_date ?? "",
                          "date"
                        ) }
                      </div>
                    </>
                  ) : (
                    <div className="no-event">{t(VehicleAndTravelDetailsMessages.noAlertsOnTravel)}</div>
                  ) }
              </div>
              <AlertModalList alerts={alerts ?? []} onlyView={onlyView} />
            </Typography>
          </AccordionDetails>
        </Accordion>
      );
    }

    return (<></>);
  }, [vehicle, onlyView, t, alerts]);

  const currentLoadContainer = useMemo(() => {

    if (isAggregateOrSiloTruck && vehicle?.current_travel?.service_order) {
      return (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography component="div">
              <div className="title">{t(VehicleAndTravelDetailsMessages.currentLoad)}</div>
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <div>
              <Typography component="div" className="current-load-information">
                <div className="current-load-information__icon">
                  { utils.getIconAccordingProductType(vehicle?.current_travel?.service_order?.product) }
                </div>
                <div className="current-load-information__details">
                  <span className="current-load-information__details--title">{ utils.getLabelAccordingProductType(
                    vehicle?.current_travel?.service_order?.product
                  ) }
                  </span>
                  <div style={{ display: "flex", gap: "0.25rem" }}>
                    <p>{ vehicle?.current_travel?.service_order?.quantity }</p>
                    <span style={{ color: "#666666" }}>{ vehicle?.current_travel?.service_order?.unit }</span>
                  </div>
                  <div className="current-load-information__details--observation">
                    { vehicle?.current_travel?.service_order?.observation }
                  </div>
                </div>
              </Typography>
            </div>
          </AccordionDetails>
        </Accordion>
      );
    }

    return (<></>);
  }, [vehicle, isAggregateOrSiloTruck, t]);

  const waterAdditionsContainer = useMemo(() => {

    if (vehicle?.type?.id_vehicle_type !== VehicleTypesID.BETONEIRA) return (<></>);

    // Water reassigned AWAY from this travel (reused in the next one) is shown only as an informational
    // block — it must NOT be counted in this travel's totals (it already counts on the destination travel).
    const ownAdditions = waterAdditions?.filter((wa) => !wa.is_reassigned_away) ?? [];

    const totalWaterAddedPostDischarge = ownAdditions.reduce(
      (acc, curr) => {
        if (curr.is_post_discharge) {
          return acc + curr.water_added;
        }

        return acc;
      }, 0
    );

    const totalWaterAddedInTravel = ownAdditions.reduce(
      (acc, curr) => acc + curr.water_added, 0
    );

    const totalWaterAddedPreDischarge = totalWaterAddedInTravel - totalWaterAddedPostDischarge;

    return (
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography component="div">
            <div className="title">{t(VehicleAndTravelDetailsMessages.waterAdded)} </div>
            {vehicle.alerts && (
              <div className="qtd">
                <Badge
                  badgeContent={ownAdditions.length}
                  color="error"
                />
              </div>
            )}
          </Typography>
        </AccordionSummary>
        <div className="subtitle">
          <div className="bold"> {t(VehicleAndTravelDetailsMessages.waterAdditionPreDischarge)}: &nbsp; </div>
          {isNaN(totalWaterAddedPreDischarge) ? 0 : utils.formatNumberToLocaleString(totalWaterAddedPreDischarge, i18n.language as TLanguages, 2) }L
        </div>
        <div className="subtitle">
          <div className="bold"> {t(VehicleAndTravelDetailsMessages.waterAdditionPostDischarge)}: &nbsp; </div>
          {utils.formatNumberToLocaleString(totalWaterAddedPostDischarge, i18n.language as TLanguages, 2) ?? 0}L
        </div>
        <div className="subtitle">
          <div className="bold"> {t(VehicleAndTravelDetailsMessages.litersAdded)}: &nbsp; </div>
          {utils.formatNumberToLocaleString(totalWaterAddedInTravel, i18n.language as TLanguages, 2) ?? 0}L
        </div>
        <AccordionDetails>
          <Typography component="div">
            <div className="date">
              {!$.isEmptyObject(vehicle)
                && travel?.water_additions?.length > 0 ? (
                  <>
                    <div className="title">{t(GlobalMessages.date)}</div>
                    <div className="text">
                      {utils.formatDateIfHave(
                        travel.water_additions[0].start_date ?? "",
                        "date"
                      )}
                    </div>
                  </>
                ) : (
                  <div className="no-event">{t(VehicleAndTravelDetailsMessages.noWaterAdded)}</div>
                )}
            </div>
            <WaterAdditionModalList waterAdditions={waterAdditions ?? []} />
          </Typography>
        </AccordionDetails>
      </Accordion>
    );

  }, [vehicle, travel, t, waterAdditions]);

  const statesContainer = useMemo(() => {

    if (vehicle) {

      const lastDueDischarge = travel?.last_due_unload_date;
      const dischargeFinishDate = vehicle?.states?.find(
        (state) => state.status.id_vehicle_state_type === VehicleStatesTypesID.DESCARREGANDO
      )?.finish_date;

      const idlePostDischarge = (lastDueDischarge && dischargeFinishDate)
        ? utils.calcDataRange(lastDueDischarge, dischargeFinishDate) as Duration
        : undefined;

      return (<TimeLineVehicleStates vehicle={vehicle} historicalTravel={historical} postUnloadIdleTime={idlePostDischarge} />);
    }

    return (<></>);
  }, [vehicle, historical]);

  /**
   * Search zones
   */
  const readZones = useCallback(async () => {
    const zones = await readZoneOverlays();

    setPolygons(zones.polygons);
    setPolylinesZMRC(zones.polylines);
  }, []);

  const mapContainer = useMemo(() => {

    if (showMap && markers.length > 0) {
      return (
        <TravelMap
          markers={markers}
          polylines={polylines}
          polygons={polygons}
          polygonPlaces={polygonPlaces}
          circles={circles}
          route={route}
          destination={destinationPoint}
          maxSpeed={travelDetails?.alert_maximum_speed || vehicle?.maximum_speed_allowed || vehicleSpeedLimit || undefined}
          vehicleTypeId={vehicle?.type?.id_vehicle_type}
          hasNote={!!travelDetails?.num_doc}
          mapFilter={mapFilter}
          setMapFilter={setMapFilter}
          details={(
            <>
              {workHeaderContainer}
              {travelDataContainer}
            </>
          )}
          t={t}
        />
      );
    }

    return (
      <div className="vehicle-travel-details__map--static">
        { loadingTravelMap
          ? <CircularProgress />
          : (
            <img
              src={LogoFleet}
              className="vehicle-travel-details__map-icon"
              alt={t(MapMessages.viewLocation)}
              onClick={() => { analyzeTravelDetail(travel); }}
              aria-hidden="true"
            />
          ) }
      </div>
    );
  }, [loadingTravelMap, analyzeTravelDetail, travel, markers, polylines, polygons, polygonPlaces, circles, route, destinationPoint, mapFilter,
    showMap, workHeaderContainer, travelDataContainer, travelDetails, vehicle, vehicleSpeedLimit, t]);

  const mainContainer = useMemo(() => {

    if (isMobile) {
      return (
        <DialogContentText tabIndex={-1} component="div">
          <div className="container">
            { workHeaderContainer }
          </div>
          <div className="vehicle-travel-details__status">
            { statusContainer }
          </div>
          <div className="container">
            { travelDataContainer }
          </div>
          <div className="container">
            <div className="block events">
              { alertsContainer }
            </div>
          </div>
          <div className="container">
            { (vehicle.type.id_vehicle_type === VehicleTypesID.CARRETA_AGREGADOS
                || vehicle.type.id_vehicle_type === VehicleTypesID.CARRETA_SILO)
              && (
                <div className="block currentLoad">
                  { currentLoadContainer }
                </div>
              ) }
          </div>
          <div className="container">
            <div className="block water-additions blue-background-badge">
              { waterAdditionsContainer }
            </div>
          </div>
          <div className="container">
            <div className="block states">
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography component="div">{t(VehicleAndTravelDetailsMessages.travelStatus)}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography component="div">
                    <div className="date">
                      <div className="title">{t(GlobalMessages.date)}</div>
                      <div className="text">
                        { !$.isEmptyObject(vehicle)
                          && utils.formatDateIfHave(
                            vehicle.states[vehicle.states.length - 1]
                              ?.registration_date,
                            "date"
                          ) }
                      </div>
                    </div>
                    { statesContainer }
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </div>
          </div>
          <div className="container">
            <div className="block vehicle-travel-details__map">
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography component="div">{t(VehicleAndTravelDetailsMessages.mapPosition)}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography component="div">
                    { mapContainer }
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </div>
          </div>
        </DialogContentText>
      );
    }

    return (
      <Grid container spacing={2} className="vehicle-travel-details__list">
        <Grid item xs={12} md={12} lg={4} className="vehicle-travel-details__item-grid">
          <div className="container vehicle-travel-details__status">
            <div className="block">
              { statusContainer }
            </div>
          </div>
          <div className="container timeline-states">
            <div className="block">
              { statesContainer }
            </div>
          </div>
        </Grid>
        <Grid item xs={12} md={12} lg={5} className="vehicle-travel-details__item-grid">
          <div className="container info">
            { workHeaderContainer }
            <div className="block vehicle-travel-details__map">
              { mapContainer }
            </div>
            { travelDataContainer }
          </div>
        </Grid>
        <Grid
          item
          xs={12}
          md={12}
          lg={3}
          className="vehicle-travel-details__item-grid vehicle-travel-details__item-grid--alerts"
        >
          <div className="vehicle-travel-details__events">
            { (vehicle.type.id_vehicle_type === VehicleTypesID.CARRETA_AGREGADOS
                || vehicle.type.id_vehicle_type === VehicleTypesID.CARRETA_SILO)
              && (
                <div className="block">
                  { currentLoadContainer }
                </div>
              ) }
            <div className="block">
              { alertsContainer }
            </div>
            <div className="block blue-background-badge">
              { waterAdditionsContainer }
            </div>
          </div>
        </Grid>
      </Grid>
    );
  }, [
    isMobile,
    vehicle,
    statusContainer,
    travelDataContainer,
    alertsContainer,
    waterAdditionsContainer,
    statesContainer,
    mapContainer,
    workHeaderContainer,
    currentLoadContainer,
    t,
    alerts
  ]);

  /**
   * Load alerts from vehicle
   */
  const getAlertFromVehicle = useCallback(async () => {

    setIsLoading(true);

    try {

      const { data } = await api.get(`alerts/read-by-vehicle-id/${vehicle.id_vehicle}`);

      if (data.status === "success") {

        return data.result as Alert[];

      } else {

        addToast({ type: "error", title: t(GlobalMessages.error), description: data.message });

        return [];
      }

    } catch (error) {

      if (!error.response) addToast({ type: "error", title: t(GlobalMessages.error), description: t(GlobalMessages.connectionNotEstablished) });
      else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });

      return [];

    } finally {
      setIsLoading(false);
    }
  }, [genericVehicle]);

  // Fill maximum_speed_allowed vehicle into alerts
  useEffect(() => {
    const fetchAlerts = async () => {
      let newAlerts = vehicle?.alerts ?? [];

      if (isVehicleAlertSummary(genericVehicle)) {
        newAlerts = await getAlertFromVehicle();
        setVehicle({ ...vehicle, alerts: newAlerts });
      }

      // 'Entrada em local confiável' and 'Abastecimento' are events, not alerts (frontend-only,
      // 10/08/2026) — they stay out of this modal and live only on the alerts-and-events history.
      newAlerts = newAlerts.filter((alert) => !NonAlertEventTypesID.includes(alert?.type?.id_alert_type ?? ""));

      const alerts = newAlerts.map((alert) => ({
        ...alert,
        vehicle: {
          ...vehicle,
          maximum_speed_allowed: vehicle.maximum_speed_allowed ?? alert.vehicle?.maximum_speed_allowed
        }
      }));

      setAlerts(alerts);
    };

    fetchAlerts().then();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [genericVehicle]);

  // Load the client's default construction radius once, so the obra circle matches the radius the
  // worker actually applied. Until it arrives the circle falls back to the system default (300 m).
  useEffect(() => {
    const fetchDefaultRadius = async () => {
      const params = await getParamsObj();
      const radius = Number(params[ParamsNames.DEFAULT_CONSTRUCTION_RADIUS]);

      if (radius) setClientDefaultRadiusMeters(radius);
    };

    fetchDefaultRadius().then();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // endregion Functions
  return (
    <Dialog
      open={open}
      onClose={() => handleClose()}
      scroll="paper"
      fullWidth
      // On desktop the details take 85% of the screen: the map, its replay and the travel data need the room
      maxWidth={isMobile ? "lg" : false}
      PaperProps={isMobile ? undefined : { style: { width: "85%" } }}
      className={`vehicle-travel-details__dialog ${overAll ? "vehicle-travel-details__dialog--overAll" : ""}`}
    >
      <Loading loading={isLoading} />
      { !isLoading && (
        <Stylesheet isMobile={isMobile} isBlocked={isBlocked}>
          <div className="vehicle-travel-details__header">
            <div className="vehicle-travel-details__title">
              <div
                className="icon"
                style={{ backgroundColor: utils.getVehicleIconColor(vehicle, user, improperDischargeAlerts) }}
              >
                { utils.getVehicleIconAccordingTypeAndAlert(vehicle, improperDischargeAlerts) }
              </div>
              <div className="content">
                <div className="content__title">
                  { vehicle.code } - { vehicle.description } - { isBlocked
                    ? <span style={{ color: "#F00", fontWeight: "bold" }}> {t(TimelineVehicleStatesMessages.blockedVehicle)}</span>
                    : (vehicle.license_plate) }
                </div>
                { vehicle?.current_location && (
                  <div className="content__subtitle">
                    {t(VehicleAndTravelDetailsMessages.allocation).toUpperCase()}:
                    { ` ${vehicle.current_location.address}, ${
                      vehicle.current_location.number || ""
                    }
                      - ${vehicle.current_location.district || ""} | ${
                    vehicle.current_location.county
                  } - ${vehicle.current_location.uf}` }
                  </div>
                ) }
              </div>
            </div>
            <div className="actions">
              { vehicle.current_travel && !travel.alert_maximum_speed && (
                <Button
                  className="create_alert_button"
                  startIcon={<AlertIconGreen width="2rem" />}
                  onClick={() => handleOpenCreateAlertSpeedModal()}
                >
                  {t(TravelAlertFullSpeedMessage.createAlertSpeedTravel)}
                  <br />
                  {t(TravelAlertFullSpeedMessage.speed)}
                </Button>
              )}
              { vehicle.current_travel && travel.alert_maximum_speed && (
                <Button
                  className="show_speed_alert_button"
                  style={{ backgroundColor: vehicle.current_speed && vehicle.current_speed > travel.alert_maximum_speed ? "red" : "green", width: "auto", textTransform: "none" }}
                  startIcon={vehicle.current_speed && vehicle.current_speed > travel.alert_maximum_speed ? <AlertIconRed width="3rem" /> : <AlertIconGreen width="3rem" />}
                  onClick={() => handleOpenCreateAlertSpeedModal()}
                  variant="contained"
                >
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <div>
                      <span style={{ fontSize: "larger" }}>{vehicle.current_speed}</span>
                      <PlayArrowIcon />
                      <span style={{ fontSize: "larger" }}>{travel.alert_maximum_speed}</span>
                      km/h
                    </div>
                    <p style={{ color: "white" }}>
                      {vehicle.current_speed && vehicle.current_speed > travel.alert_maximum_speed
                        ? `${t(GlobalMessages.speed)} ${t(GlobalMessages.exceededPT)} ` : `${t(GlobalMessages.speed)} Nominal`}
                    </p>
                  </div>
                </Button>
              )}

              <Button
                className="maintenance_button"
                startIcon={<ManutencaoIcon width="2rem" />}
                onClick={() => handleOpenVehicleMaintenanceModal()}
                variant="contained"
              >
                {t(VehicleMaintenanceMessages.maintenance)}
              </Button>
              { user.allowed_vehicle_block && vehicle.hardwares?.[0]?.block_capable && (
                <div>
                  <Button
                    className="block_button"
                    startIcon={isBlocked
                      ? <UnblockVehicleIcon width="2rem" />
                      : <BlockVehicleIcon width="2rem" />}
                    onClick={() => handleOpenVehicleBlockModal()}
                    variant="contained"
                  >
                    { isBlocked ? t(VehicleAndTravelDetailsMessages.unlockVehicle) : t(VehicleAndTravelDetailsMessages.lockVehicle) }
                  </Button>
                </div>
              ) }
            </div>
          </div>

          <DialogContent
            dividers
            className={`vehicle-travel-details__content vehicle-travel-details__content--${isMobile
              ? "mobile" : "desktop"}`}
          >
            { mainContainer }
          </DialogContent>
          <DialogActions className="vehicle-travel-details__footer">
            <Button disableRipple onClick={() => handleClose()} color="primary">
              {t(GlobalMessages.close)}
            </Button>
          </DialogActions>
          <ModalVehicleBlock
            open={openModalVehicleBlock}
            onClose={() => {
              setOpenModalVehicleBlock(false);
              handleClose();
            }}
            vehicle={vehicle}
            isBlocked={isBlocked}
          />
          <ModalVehicleMaintenance
            open={openModalVehicleMaintenance}
            onClose={() => {
              setOpenModalVehicleMaintenance(false);
              handleClose();
            }}
            vehicle={vehicle}
            isInMaintenance={vehicle.maintenance}
          />

          <ModalSpeedTravel
            open={openModalCreateAlert}
            onClose={() => {
              setOpenModalCreateAlertSpeed(false);
              handleClose();
            }}
            travel={travel}
            vehicle={vehicle}
          />
        </Stylesheet>
      )}
    </Dialog>
  );
};

export {
  ModalVehicleAndTravelDetails
};
